import { h } from "vue";
import { NTag } from "naive-ui";
import type { Comment } from "../models/contador";

type NotesRow = {
    comments?: Comment[] | null;
};

type NotesCellOptions<T extends NotesRow> = {
    row: T;
    onOpenNotes: (row: T) => void;
    getComments?: (row: T) => Comment[] | null | undefined;
};

function htmlToText(html: string | null | undefined): string {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    return (div.textContent || "").replace(/\s+/g, " ").trim();
}

function extractImageSrcs(html: string | null | undefined): string[] {
    if (!html) return [];
    const div = document.createElement("div");
    div.innerHTML = html;
    const imgs = Array.from(div.querySelectorAll("img"));
    return imgs.map((img) => img.getAttribute("src") || "").filter((src) => !!src);
}

export function renderNotesCell<T extends NotesRow>({
    row,
    onOpenNotes,
    getComments,
}: NotesCellOptions<T>) {
    const comments = getComments ? getComments(row) : row.comments;
    const unarchived = (comments || [])
        .filter((comment) => !comment.archived)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    if (unarchived.length === 0) {
        return h(
            "button",
            {
                class: "flex items-center justify-center gap-0.5 text-neutral-400 hover:text-neutral-700 active:text-neutral-900 active:scale-95 transition",
                onClick: () => onOpenNotes(row),
            },
            [
                h("span", { class: "material-symbols-outlined text-base" }, "add "),
                "Add Note",
            ],
        );
    }

    const latest = unarchived[0];
    const text = htmlToText(latest.text).trim();
    const imgs = extractImageSrcs(latest.text);
    const moreCount = unarchived.length - 1;

    let displayContent: any;
    if (text.length > 0) {
        displayContent = h(
            "span",
            { class: "whitespace-nowrap overflow-hidden text-ellipsis" },
            text,
        );
    } else if (imgs.length > 0) {
        displayContent = h("img", {
            src: imgs[0],
            alt: "Comment image",
            class: "inline-block align-middle",
            style: { maxHeight: "40px", maxWidth: "40px", objectFit: "cover" },
        });
    } else {
        displayContent = h("span", { class: "text-neutral-400" }, "(empty)");
    }

    const contentWrapperChildren = [displayContent];
    if (moreCount > 0) {
        const moreTag = h(
            NTag,
            { size: "small", type: "default", class: "ml-2" },
            { default: () => `+${moreCount}` },
        );
        contentWrapperChildren.push(moreTag as any);
    }

    return h(
        "button",
        {
            class: "flex items-center w-full text-left hover:opacity-80",
            onClick: () => onOpenNotes(row),
        },
        contentWrapperChildren,
    );
}
